<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("menu.app_catalog_platform_category_map") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1">
                <template slot="header">
                    <div class="row align-items-center">
                        <div class="col-md-4">
                            <label class="form-control-label">{{ $t("amaze_push.platform_label") }}</label>
                            <el-select v-model="platformId" class="w-100" @change="reload">
                                <el-option
                                    v-for="p in platforms"
                                    :key="p.id"
                                    :label="$t(p.labelKey)"
                                    :value="p.id"
                                    :disabled="!p.enabled"
                                ></el-option>
                            </el-select>
                        </div>
                        <div class="col-md-8">
                            <p class="mb-0 text-muted small">{{ $t("amaze_push.map.hint") }}</p>
                            <p class="mb-0 small" v-if="autoApprove">
                                <span class="text-danger font-weight-bold">
                                    <i class="fa fa-exclamation-triangle mr-1"></i>
                                    {{ $t("amaze_push.map.auto_approve_warning") }}
                                </span>
                            </p>
                        </div>
                    </div>
                </template>

                <!-- The three 503s stay three different sentences: "switched
                     off" and "no credentials" looking the same is what sent an
                     operator through the wrong config file on the wrong host. -->
                <div class="px-4 pb-3" v-if="referenceErrorKey">
                    <div class="alert alert-warning py-2 px-3 mb-0">
                        <i class="fa fa-exclamation-triangle mr-1"></i>
                        {{ $t(referenceErrorKey) }}
                    </div>
                </div>

                <div v-loading="loading">
                    <el-table
                        :data="categories"
                        row-key="id"
                        size="small"
                        header-row-class-name="thead-light"
                        :row-class-name="rowClassName"
                        :empty-text="$t('app_catalog.no_data')"
                    >
                        <el-table-column :label="$t('amaze_push.map.col_seller_category')" min-width="180">
                            <template slot-scope="{ row }">
                                {{ row.name_th || row.name }}
                                <el-tag v-if="!isMapped(row.id)" type="warning" size="mini" class="ml-1">
                                    {{ $t("amaze_push.map.unmapped") }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('amaze_push.map.col_platform_category')" min-width="220">
                            <template slot-scope="{ row }">
                                <span class="small text-monospace">{{ mappedPathLabel(row.id) }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('amaze_push.map.col_updated_at')" min-width="150">
                            <template slot-scope="{ row }">
                                <span class="small text-muted">{{ mappedUpdatedAt(row.id) }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('actions')" width="120" align="right">
                            <template slot-scope="{ row }">
                                <base-button size="sm" type="warning" @click="openEditor(row)">
                                    <i class="fa fa-edit"></i>
                                </base-button>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
            </card>
        </div>

        <!-- editor -->
        <modal :show.sync="editorOpen" modal-classes="modal-lg platform-category-map-modal">
            <h5 slot="header" class="modal-title">
                {{ $t("amaze_push.map.editor_title") }} — {{ editing ? editing.name_th || editing.name : "" }}
            </h5>

            <div v-loading="referenceLoading">
                <div class="row mb-3">
                    <div class="col-md-12">
                        <label class="form-control-label">
                            {{ $t("amaze_push.map.platform_category") }}
                            <span class="text-danger">*</span>
                        </label>
                        <el-cascader
                            v-model="form.category_path"
                            :options="categoryOptions"
                            :props="cascaderProps"
                            class="w-100"
                            filterable
                            change-on-select
                            @change="onCategoryChange"
                        ></el-cascader>
                        <small class="text-muted d-block">{{ $t("amaze_push.map.category_hint") }}</small>
                        <small class="text-danger d-block" v-if="!pathValid">
                            {{ $t("amaze_push.map.category_required") }}
                        </small>
                    </div>
                </div>

                <div class="row mb-3">
                    <div class="col-md-6">
                        <label class="form-control-label">{{ $t("amaze_push.map.brand") }}</label>
                        <el-select v-model="form.brand_id" class="w-100" filterable clearable>
                            <el-option
                                v-for="b in reference.brands"
                                :key="b.id"
                                :label="b.name"
                                :value="b.id"
                            ></el-option>
                        </el-select>
                    </div>
                    <div class="col-md-6">
                        <label class="form-control-label">{{ $t("amaze_push.map.warehouse") }}</label>
                        <el-select v-model="form.inventory_shop_address_id" class="w-100" clearable>
                            <el-option
                                v-for="a in reference.shop_addresses"
                                :key="a.id"
                                :label="a.full_name"
                                :value="a.id"
                            ></el-option>
                        </el-select>
                    </div>
                </div>

                <div class="row mb-3">
                    <div class="col-md-6">
                        <label class="form-control-label">{{ $t("amaze_push.map.shipping") }}</label>
                        <el-select v-model="form.shipping_method_id" class="w-100" clearable>
                            <el-option
                                v-for="m in reference.shipping_methods"
                                :key="m.shop_shipping_method_id"
                                :label="m.name || m.code_name"
                                :value="m.shop_shipping_method_id"
                            ></el-option>
                        </el-select>
                    </div>
                    <div class="col-md-6">
                        <label class="form-control-label">{{ $t("amaze_push.map.warehouse_name") }}</label>
                        <el-input v-model="form.inventory_warehouse_name"></el-input>
                    </div>
                </div>

                <!-- Mandatory attributes. §6B.3 rule 6 turns a missing one into
                     a product-level `missing_required_attribute` reject at push
                     time, so this page is the last chance to ask. -->
                <div class="row mb-3" v-if="mandatoryAttrs.length">
                    <div class="col-md-12">
                        <label class="form-control-label">{{ $t("amaze_push.map.attributes") }}</label>
                        <div v-for="attr in mandatoryAttrs" :key="attr.attribute_id" class="mb-2">
                            <label class="form-control-label small mb-0">
                                {{ attr.name }} <span class="text-danger">*</span>
                            </label>
                            <el-select
                                v-model="form.attribute_values[attr.attribute_id]"
                                class="w-100"
                                filterable
                                clearable
                            >
                                <el-option
                                    v-for="opt in optionsOf(attr)"
                                    :key="opt.value"
                                    :label="opt.label"
                                    :value="opt.value"
                                ></el-option>
                            </el-select>
                        </div>
                    </div>
                </div>

                <!-- payment_configs has NO endpoint on Amaze (§6B.2): its only
                     source is a value copied off an existing product by the
                     §6B.6 runbook. Rather than invent an id, the page shows
                     what is already saved and says where it comes from. -->
                <div class="row mb-3">
                    <div class="col-md-12">
                        <label class="form-control-label">{{ $t("amaze_push.map.payment_configs") }}</label>
                        <p class="mb-0 small text-monospace">{{ paymentConfigsLabel }}</p>
                        <small class="text-muted">{{ $t("amaze_push.map.payment_configs_hint") }}</small>
                    </div>
                </div>

                <div class="row mb-3">
                    <div class="col-md-3">
                        <label class="form-control-label">{{ $t("amaze_push.map.weight") }}</label>
                        <el-input v-model.number="form.weight"></el-input>
                    </div>
                    <div class="col-md-3">
                        <label class="form-control-label">{{ $t("amaze_push.map.parcel_width") }}</label>
                        <el-input v-model.number="form.parcel_width"></el-input>
                    </div>
                    <div class="col-md-3">
                        <label class="form-control-label">{{ $t("amaze_push.map.parcel_length") }}</label>
                        <el-input v-model.number="form.parcel_length"></el-input>
                    </div>
                    <div class="col-md-3">
                        <label class="form-control-label">{{ $t("amaze_push.map.parcel_height") }}</label>
                        <el-input v-model.number="form.parcel_height"></el-input>
                    </div>
                </div>

                <div class="row" v-if="saveErrorKey">
                    <div class="col-md-12">
                        <div class="alert alert-danger py-2 px-3 mb-0">
                            <i class="fa fa-exclamation-triangle mr-1"></i>
                            {{ $t(saveErrorKey) }}
                        </div>
                    </div>
                </div>
            </div>

            <template slot="footer">
                <button
                    type="button"
                    class="btn btn-md btn-success"
                    :disabled="saving || !pathValid"
                    @click="save()"
                >
                    <i class="fa fa-spinner fa-spin mr-1" v-if="saving"></i>
                    {{ $t("amaze_push.map.btn_save") }}
                </button>
                <button type="button" class="btn btn-md btn-link ml-auto" @click="editorOpen = false">
                    {{ $t("close") }}
                </button>
            </template>
        </modal>
    </div>
</template>

<script>
// #263 — "จับคู่หมวด platform" (spec §6B.8, endpoints §6B.4).
//
// WHAT THIS PAGE IS FOR: Amaze refuses a product without a destination
// category, a brand, the category's mandatory attributes, a warehouse, a
// shipping method and a payment-channel config — all of them UUIDs that exist
// only on Amaze and differ per shop. Asking for that on every push would be
// absurd, so it is answered once per seller category here and the push modal
// only asks for per-product overrides.
//
// NOTHING ON THIS PAGE IS HARD-CODED. §6B.2 is explicit that every value in
// default_attributes is a platform id ("ห้าม hard-code ในโค้ด"), so every
// dropdown is filled from GET /api/platform/v2/amaze/reference, which reads
// them live from Amaze (cached an hour on the BE).
//
// THE ONE VALUE WITH NO ENDPOINT: payment_configs[].shop_payment_channel_config_id.
// §6B.2's provenance table says there is no API that lists payment channels,
// and its only source is a value copied off an existing Amaze product by the
// §6B.6 runbook. The page therefore DISPLAYS what is already saved and refuses
// to invent one — a fabricated id here would be rejected at push time with an
// error about a channel the operator never chose.
import { apiErrorMessage } from "@/util/api-error";
import { mapGetters, mapActions } from "vuex";
import { Select, Option, Table, TableColumn, Tag, Cascader, Input } from "element-ui";
import { PlatformAmazeService } from "@/resource";
import {
    AMAZE_PLATFORM_ID,
    PUSH_PLATFORMS,
    categoriesToCascaderOptions,
    mandatoryAttributes,
    attributeOptions,
    buildDefaultAttributes,
    categoryMapBody,
    isValidCascaderPath,
    indexCategoryMaps,
    isCategoryMapped,
    categoryMapErrorI18nKey,
    pushErrorI18nKey
} from "@/utils/amazePush";

export default {
    name: "AppCatalogPlatformCategoryMap",
    components: {
        [Select.name]: Select,
        [Option.name]: Option,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag,
        [Cascader.name]: Cascader,
        [Input.name]: Input
    },
    data() {
        return {
            platformId: AMAZE_PLATFORM_ID,
            platforms: PUSH_PLATFORMS,
            loading: false,
            referenceLoading: false,
            saving: false,
            maps: {},
            reference: {
                categories: [],
                attributes: [],
                brands: [],
                shop_addresses: [],
                shipping_methods: [],
                auto_approve: false
            },
            referenceErrorKey: "",
            saveErrorKey: "",
            editorOpen: false,
            editing: null,
            cascaderProps: { value: "value", label: "label", children: "children" },
            form: this.emptyForm()
        };
    },
    computed: {
        ...mapGetters({ categories: "AppCatalogCategory/list" }),
        categoryOptions() {
            return categoriesToCascaderOptions(this.reference.categories);
        },
        mandatoryAttrs() {
            return mandatoryAttributes(this.reference.attributes);
        },
        autoApprove() {
            return this.reference.auto_approve === true;
        },
        pathValid() {
            return isValidCascaderPath(this.form.category_path);
        },
        paymentConfigsLabel() {
            const list = this.form.payment_configs || [];
            if (!list.length) return "-";
            return list.map((p) => p.shop_payment_channel_config_id || "?").join(", ");
        }
    },
    mounted() {
        this.reload();
    },
    methods: {
        ...mapActions({ fetchCategories: "AppCatalogCategory/fetchList" }),
        emptyForm() {
            return {
                category_path: [],
                brand_id: "",
                inventory_shop_address_id: "",
                inventory_warehouse_name: "",
                shipping_method_id: "",
                attribute_values: {},
                payment_configs: [],
                weight: 0,
                parcel_width: 0,
                parcel_length: 0,
                parcel_height: 0
            };
        },
        async reload() {
            this.loading = true;
            try {
                await this.fetchCategories({});
                const res = await PlatformAmazeService.categoryMapList(this.platformId);
                this.maps = indexCategoryMaps(res && res.data);
            } catch (err) {
                this.$notify({ type: "danger", message: apiErrorMessage(err, this.$t) });
            } finally {
                this.loading = false;
            }
            this.loadReference("");
        },
        // The reference is loaded WITHOUT a category first (the cascader tree,
        // the warehouses, the shipping methods) and again WITH one as soon as a
        // category is chosen, because Amaze attaches attributes and brands to
        // the leaf category only.
        async loadReference(categoryGroupId) {
            this.referenceLoading = true;
            this.referenceErrorKey = "";
            try {
                const res = await PlatformAmazeService.reference(categoryGroupId);
                const data = (res && res.data) || {};
                this.reference = {
                    categories: data.categories || [],
                    attributes: data.attributes || [],
                    brands: data.brands || [],
                    shop_addresses: data.shop_addresses || [],
                    shipping_methods: data.shipping_methods || [],
                    auto_approve: data.auto_approve === true
                };
            } catch (err) {
                this.referenceErrorKey = pushErrorI18nKey(err);
            } finally {
                this.referenceLoading = false;
            }
        },
        isMapped(categoryId) {
            return isCategoryMapped(this.maps, categoryId);
        },
        // Warning colour on the rows that are not mapped yet (§6B.8).
        rowClassName({ row }) {
            return this.isMapped(row.id) ? "" : "platform-category-unmapped";
        },
        mappedPathLabel(categoryId) {
            const row = this.maps[Number(categoryId)];
            if (!row) return "-";
            const path = (row.platform_category || {}).category_path || [];
            return path.length ? path.join(" › ") : "-";
        },
        mappedUpdatedAt(categoryId) {
            const row = this.maps[Number(categoryId)];
            return (row && row.updated_at) || "-";
        },
        optionsOf(attribute) {
            return attributeOptions(attribute);
        },
        openEditor(category) {
            this.editing = category;
            this.saveErrorKey = "";
            this.form = this.emptyForm();

            const saved = this.maps[Number(category.id)];
            if (saved) {
                const pc = saved.platform_category || {};
                const defaults = saved.default_attributes || {};
                this.form.category_path = Array.isArray(pc.category_path) ? pc.category_path.slice() : [];
                this.form.brand_id = defaults.brand_id || "";
                this.form.inventory_shop_address_id = defaults.inventory_shop_address_id || "";
                this.form.inventory_warehouse_name = defaults.inventory_warehouse_name || "";
                this.form.payment_configs = Array.isArray(defaults.payment_configs)
                    ? defaults.payment_configs
                    : [];
                this.form.weight = Number(defaults.weight) || 0;
                const parcel = defaults.parcel_size || {};
                this.form.parcel_width = Number(parcel.width) || 0;
                this.form.parcel_length = Number(parcel.length) || 0;
                this.form.parcel_height = Number(parcel.height) || 0;
                const shipping = Array.isArray(defaults.shipping) ? defaults.shipping[0] : null;
                this.form.shipping_method_id = (shipping && shipping.shop_shipping_method_id) || "";
                for (const attr of defaults.attributes || []) {
                    const ids = attr.attribute_option_ids || [];
                    if (ids.length) this.$set(this.form.attribute_values, attr.attribute_id, ids[0]);
                }
            }

            this.editorOpen = true;
            // Reload attributes/brands for whichever category is already saved,
            // so the mandatory inputs show up on open rather than only after
            // the operator touches the cascader.
            const leaf = this.form.category_path.length
                ? this.form.category_path[this.form.category_path.length - 1]
                : "";
            this.loadReference(leaf);
        },
        onCategoryChange(path) {
            const leaf = Array.isArray(path) && path.length ? path[path.length - 1] : "";
            this.loadReference(leaf);
        },
        selectedShippingMethod() {
            const id = this.form.shipping_method_id;
            if (!id) return null;
            return this.reference.shipping_methods.find((m) => m.shop_shipping_method_id === id) || null;
        },
        async save() {
            if (!this.pathValid || !this.editing) return;
            this.saving = true;
            this.saveErrorKey = "";

            const attributes = {};
            for (const attr of this.mandatoryAttrs) {
                const chosen = this.form.attribute_values[attr.attribute_id];
                if (chosen) attributes[attr.attribute_id] = { option_ids: [chosen] };
            }

            const defaults = buildDefaultAttributes({
                brand_id: this.form.brand_id,
                attributes: attributes,
                shipping_method: this.selectedShippingMethod(),
                payment_configs: this.form.payment_configs,
                inventory_shop_address_id: this.form.inventory_shop_address_id,
                inventory_warehouse_name: this.form.inventory_warehouse_name,
                weight: this.form.weight,
                parcel_width: this.form.parcel_width,
                parcel_length: this.form.parcel_length,
                parcel_height: this.form.parcel_height
            });

            try {
                await PlatformAmazeService.categoryMapSave(
                    categoryMapBody(this.platformId, this.editing.id, this.form.category_path, defaults)
                );
                this.$notify({ type: "success", message: this.$t("amaze_push.map.saved") });
                this.editorOpen = false;
                await this.reload();
            } catch (err) {
                this.saveErrorKey = categoryMapErrorI18nKey(err);
            } finally {
                this.saving = false;
            }
        }
    }
};
</script>

<style>
/* Unmapped seller categories are the whole point of the page, so they are
   tinted rather than only tagged. */
.el-table .platform-category-unmapped {
    background-color: #fff8e1;
}
.platform-category-map-modal .modal-body {
    max-height: 70vh;
    overflow-y: auto;
}
</style>
